import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, startWith, withLatestFrom } from 'rxjs/operators';
import { BaseFormControlComponent } from '../../../shared/components/base-form-control/base-form-control.component';
import { ValidityIconComponent } from '../../../shared/components/validity-icon/validity-icon.component';
import { FormControlDirective } from '../../../shared/directives/form-control/form-control.directive';
@Component({
  selector: 'app-form-sortcode-input',
  templateUrl: './sortcode-input.component.html',
  styleUrls: ['./sortcode-input.component.scss'],
  providers: [
    {
      provide: BaseFormControlComponent,
      useExisting: forwardRef(() => SortcodeInputComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    FormControlDirective,
    ValidityIconComponent,
    ReactiveFormsModule,
  ],
  standalone: true,
})
export class SortcodeInputComponent
  extends BaseFormControlComponent<string>
  implements OnInit
{
  @ViewChild('sort1') sort1: ElementRef | undefined;
  @ViewChild('sort2') sort2: ElementRef | undefined;
  @ViewChild('sort3') sort3: ElementRef | undefined;

  sort1Subject = new BehaviorSubject<string>('');
  sort2Subject = new BehaviorSubject<string>('');
  sort3Subject = new BehaviorSubject<string>('');
  sortcode$!: Observable<string | undefined>;
  sortOneId!: string;
  sortTwoId!: string;
  sortThreeId!: string;

  private readonly elementRef = inject(ElementRef);

  override ngOnInit(): void {
    super.ngOnInit();
    this.controlFocus();
    this.buildControlValues();
    this.buildSortcode();
    this.bindControlValueToSegments();
    this.bindSortcodeToControl();
  }

  onSortOneChange(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    this.sort1Subject.next(target.value);
  }

  onSortTwoChange(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    this.sort2Subject.next(target.value);
  }

  onSortThreeChange(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    this.sort3Subject.next(target.value);
  }

  @HostListener('focusout', ['$event'])
  onFocusOut = (event: FocusEvent): void => {
    const target: HTMLElement = event.relatedTarget as HTMLElement;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.onBlur();
    }
  };

  private buildControlValues(): void {
    this.sortOneId = `${this.id}--one`;
    this.sortTwoId = `${this.id}--two`;
    this.sortThreeId = `${this.id}--three`;
  }

  private buildSortcode(): void {
    this.sortcode$ = combineLatest([
      this.sort1Subject,
      this.sort2Subject,
      this.sort3Subject,
    ]).pipe(
      map((segments: string[]) =>
        segments.every((segment: string) => segment.length === 2)
          ? segments.join('')
          : undefined,
      ),
    );
  }

  private bindSortcodeToControl(): void {
    this.sortcode$
      .pipe(
        filter((sortcode?: string) =>
          this.compareSortcodeAndValue(sortcode, this.value),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sortcode?: string) => {
        this.setValue(sortcode);
        this.changeDetectorRef.markForCheck();
      });
  }

  private bindControlValueToSegments(): void {
    this.valueChanges
      .pipe(
        startWith(this.value),
        map((value: string | undefined) => value?.toString()),
        withLatestFrom(this.sortcode$),
        filter(([value, sortcode]: (string | undefined)[]) =>
          this.compareSortcodeAndValue(sortcode, value),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([value]: (string | undefined)[]) => {
        if (value) {
          this.sort1Subject.next(value.substring(0, 2));
          this.sort2Subject.next(value.substring(2, 4));
          this.sort3Subject.next(value.substring(4, 6));
        } else {
          this.sort1Subject.next('');
          this.sort2Subject.next('');
          this.sort3Subject.next('');
        }
        this.changeDetectorRef.markForCheck();
      });
  }

  private compareSortcodeAndValue(
    sortcode: string | undefined,
    value: string | undefined,
  ): boolean {
    return sortcode !== value;
  }

  controlFocus() {
    combineLatest([this.sort1Subject, this.sort2Subject, this.sort3Subject])
      .pipe(
        map(([sort1, sort2, sort3]) => [
          sort1.length === 2 && sort2.length !== 2,
          sort2.length === 2 && sort3.length !== 2,
        ]),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([focusSort2, focusSort3]) => {
        if (this.sort2 && focusSort2) {
          this.sort2.nativeElement.focus();
        }

        if (this.sort3 && focusSort3) {
          this.sort3.nativeElement.focus();
        }

        this.changeDetectorRef.markForCheck();
      });
  }
}
